import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MedReviewService } from '../../services/med-review.service';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { of } from 'rxjs';
import { retry, take, tap } from 'rxjs/operators';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { LayoutUtilsService, MessageType } from '../../../../../core/_base/crud';
import { select, Store } from '@ngrx/store';
import { currentUser } from '../../../../../core/auth';
import { AppState } from '../../../../../core/reducers';
import { TemplateService } from '../../../../../core/lists/_services/template.service';
import { TaskService } from '../../../../../core/timeline';
import { Task } from '../../../calendar/models';
import { TaskDefaultView, TaskPriority, TaskStatus } from '../../../calendar/enums';

@Component({
  selector: 'kt-manage-problem',
  templateUrl: './manage-problem.component.html',
})
export class ManageProblemComponent implements OnInit {
  interventionBody = [];
  @ViewChild('teamAutocomplete') teamAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('interventionCodeAutocomplete')
  interventionCodeAutocomplete: GeneralAutocompleteComponent;
  currentUser;
  hasFormErrors = false;
  error = '';
  private interventionForMap = [];
  private defaultTeams: any;
  templateAutocompleteCall;
  teamExist = true;
  @ViewChild('templateAutocomplete') templateAutocomplete: GeneralAutocompleteComponent;
  fillDatePlz = false;
  levelExist = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ManageProblemComponent>,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private medReviewService: MedReviewService,
    public stringUtils: StringUtilsService,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private templateService: TemplateService,
    private taskService: TaskService,
  ) {}

  manageForm: FormGroup;
  duration = 0;
  drug;
  durationCtrl = new FormControl();
  causes = [{ id: 1, cause_code: 'test' }];
  intervention_levels = [{ id: 1, level_code: 0, level: 'No intervention' }];
  interventions = [];
  filterInterventionCodeFn = this.medReviewService.getInterventionCodesAutocomplete.bind(
    this.medReviewService,
    '',
  );
  filterTeamFn = this.medReviewService.getTeamAutocomplete.bind(this.medReviewService);
  prescription: any;
  requiredTeam = false;
  requiredTemplate = false;
  requiredIntCode = false;
  loadItems = (t) => {
    return this.medReviewService.getCauseAutocomplete(this.data.drp.problem_code, t).pipe(
      tap((data) => {
        data.map((val: any) => {
          return {
            intervention_values: val.intervention_values,
            description: val.description,
            cause_code: val.cause_code,
            id: val.id,
          };
        });
      }),
    );
  };

  ngOnInit(): void {
    this.store.pipe(select(currentUser)).subscribe((res) => {
      if (res) {
        this.currentUser = res;
      }
    });
    this.medReviewService.getInterventionLevelsSelect().subscribe((res) => {
      this.intervention_levels = res;
    });
    const codes = [this.data.drp.drug_code];
    if (codes.length > 0)
      this.medReviewService.getDrugByNDC(codes).subscribe((res) => {
        if (res) {
          this.drug = res.data[0];
          const drugIds = [];
          res.data.forEach((element) => drugIds.push(element.id));
          this.medReviewService
            .getPrescriptionByPatientAndDrug(this.data.patient.id, drugIds)
            .subscribe((result) => {
              if (result) this.prescription = result.data[0];
            });
        }
      });
    this.manageForm = this.fb.group({
      due_date: [undefined, Validators.required],
      causes: [],
      template: [],
      team_responsible: [],
      intervention_level: [undefined, Validators.required],
      intervention_code: [],
      note: [undefined, Validators.required],
    });
    this.medReviewService.getDefaultTeam().subscribe((result) => {
      if (result && result[0] && result[0].data) {
        this.defaultTeams = result[0].data;
        this.manageForm.controls.team_responsible.setValue(this.defaultTeams);
        this.teamAutocomplete.setValue(this.defaultTeams);
      }
    });

    this.templateAutocompleteCall = this.templateService.findTaskTemplates.bind(
      this.templateService,
      this.getTemplatesFilter(),
    );

    this.fillDueDate();
  }

  getTemplatesFilter() {
    return { categoryId: this.manageForm.get('team_responsible').value?.id, isIntervention: true };
  }

  fillDueDate() {
    const due_date = new Date();
    const future_date = new Date();
    future_date.setDate(future_date.getDate() + 7);
    const facility_cycle_date = new Date(new Date(this.data.facility_cycle_date + ' UTC'));
    // Today + 5 days if facility cycle day is less than 1 week in the future or there is no facility cycle date for the patient.
    if (!this.data.facility_cycle_date || facility_cycle_date < future_date) {
      due_date.setDate(due_date.getDate() + 5);
      this.manageForm.controls.due_date.setValue(due_date);
      return;
    }
    // Facility Cycle Date – 2 days if facility cycle date.
    const new_due_date = new Date(new Date(this.data.facility_cycle_date + ' UTC'));
    new_due_date.setDate(new_due_date.getDate() - 2);
    this.manageForm.controls.due_date.setValue(new_due_date);
  }
  close() {
    this.dialogRef.close({ refresh: true });
  }
  levelSelected() {
    this.filterInterventionCodeFn = this.medReviewService.getInterventionCodesAutocomplete.bind(
      this.medReviewService,
      this.manageForm.controls.intervention_level.value,
    );
    this.manageForm.controls.intervention_code.setValue(null);
    this.interventionCodeAutocomplete.setValue(null);
    this.levelExist = false;
  }
  onFillClose() {
    this.fillDatePlz = false;
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.manageForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  save() {
    if (this.interventions.length === 0) {
      this.hasFormErrors = true;
      this.error = 'Please Add Interventions';
      return;
    }
    const controls = this.manageForm.controls;
    controls.intervention_level.clearValidators();
    controls.intervention_level.updateValueAndValidity();
    controls.note.clearValidators();
    controls.note.updateValueAndValidity();
    if (this.manageForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    const d = new Date();
    // tslint:disable-next-line:radix
    d.setMonth(d.getMonth() + parseInt(this.durationCtrl.value));
    const causes = [];
    if (this.manageForm.controls.causes.value)
      this.manageForm.controls.causes.value.forEach((ele) => {
        causes.push(ele.id);
      });
    const body = {
      causes,
      interventions: this.interventionBody,
    };
    this.medReviewService
      .manageDRP(body, this.data.patient.id, this.data.drp.id)
      .subscribe((res) => {
        this.layoutUtilsService.showActionNotification(
          'DRP Managed Successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        this.interventionForMap = res;
        this.dialogRef.close({ refresh: true });
        this.createTask();
      });
  }

  getTitle() {
    if (this.data.from === 'manage')
      return 'Manage Drug Related Problem - ' + (this.drug ? this.drug.drug : '');
    if (this.data.from === 'ignore') return 'Ignore DRP - ' + (this.drug ? this.drug.drug : '');
  }

  addIntervention() {
    this.fillDatePlz = false;
    if (!this.manageForm.controls.due_date.value) {
      this.fillDatePlz = true;
      this.manageForm.controls.due_date.markAsTouched();
      return;
    }
    const team_responsible = this.manageForm.get('team_responsible').value;
    const intervention_code = this.manageForm.get('intervention_code').value;
    const template = this.manageForm.get('template').value;
    const note = this.manageForm.get('note').value;
    const intervention_level = this.manageForm.get('intervention_level').value;
    const due_date = this.manageForm.get('due_date').value;
    if (!team_responsible || !intervention_code || !note || !template || !intervention_level) {
      const controls = this.manageForm.controls;
      controls.intervention_level.setValidators([Validators.required]);
      controls.intervention_level.updateValueAndValidity();
      controls.note.setValidators([Validators.required]);
      controls.note.updateValueAndValidity();
      if (!controls.team_responsible.value) {
        this.requiredTeam = true;
        this.teamAutocomplete.updateValueAndValidity();
      }
      if (!controls.template.value) {
        this.requiredTemplate = true;
        this.templateAutocomplete.updateValueAndValidity();
      }
      if (!controls.intervention_code.value) {
        this.requiredIntCode = true;
        this.interventionCodeAutocomplete.updateValueAndValidity();
      }
      if (!controls.note.value) {
        controls.note.markAsTouched();
      }
      if (!controls.intervention_level.value) {
        controls.intervention_level.markAsTouched();
      }

      return;
    }
    this.interventions.push({
      team_responsible,
      due_date,
      intervention_code,
      note,
    });
    this.interventionBody.push({
      team_responsible: team_responsible.id,
      due_date: this.datePipe.transform(due_date, 'yyyy-MM-dd'),
      intervention_code: intervention_code.id,
      note,
      doctor_id: this.prescription && this.prescription.doctor ? this.prescription.doctor.id : null,
      template_id: template.id,
    });
    this.requiredIntCode = false;
    this.manageForm.controls.intervention_code.setValue(null);
    this.interventionCodeAutocomplete.setValue(null);
    this.manageForm.get('note').reset();
  }

  selectInterventionCode(val) {
    this.manageForm.controls.intervention_code.setValue(val);
  }

  selectTeam(val) {
    this.manageForm.controls.team_responsible.setValue(val);
    this.teamExist = true;
    if (val) {
      this.teamExist = false;
    }

    this.templateAutocompleteCall = this.templateService.findTaskTemplates.bind(
      this.templateService,
      this.getTemplatesFilter(),
    );
  }

  filterCauseFn(cause: any): string {
    let res = '';
    if (cause && cause.description) {
      res += cause.description;
    }
    return res;
  }

  onCauseChange(value) {
    const causes = value.map((val) => {
      if (val && !val.id) {
        return {
          intervention_values: val.intervention_values,
          description: val.description,
          cause_code: val.cause_code,
          id: val.id,
        };
      }
      return val;
    });
    this.manageForm.controls.causes.setValue(causes);
    this.manageForm.controls.causes.updateValueAndValidity();
  }

  formatNewCauseFn(cause: any) {
    return cause;
  }

  createTask(): void {
    if (this.interventionForMap.length <= 0) return;

    const groupedByInterventionCodeLevel = this.groupBy(
      this.interventionForMap,
      (ele) => ele.intervention_code.level.id,
    );

    for (const level of groupedByInterventionCodeLevel.keys()) {
      const interventions = groupedByInterventionCodeLevel.get(level);

      for (const intervention of interventions) {
        const getTemplate$ = intervention.template_id
          ? this.templateService.getTaskTemplate(intervention.template_id)
          : of({ data: null });

        getTemplate$.pipe(take(1)).subscribe((res1) => {
          const template = res1?.data;
          const task: Task = {
            id: null,
            task_entities: [{ entity_id: this.data.patient?.id }],
            task_tags: [],
            allow_payment: res1?.data?.allow_payment,
            task_attachments: [],
            steps: [
              {
                id: null,
                title: `DRP ${intervention.intervention_code.code} - ${this.drug?.drug || ''}`,
                order: 1,
                default_view: TaskDefaultView.INTERVENTION,
                priority: TaskPriority.MEDIUM,
                progress: TaskStatus.PLANNED,
                office_id: null,
                description: this.data.drp.text,
                url_resource: null,
                due_date: this.manageForm.value.due_date
                  ? this.datePipe.transform(
                      this.manageForm.value.due_date,
                      'yyyy-MM-dd HH:mm:ss',
                      'UTC',
                    )
                  : null,
                sequential_order: false,
                task_template: template,
                task_followers: [{ follower_id: this.currentUser?.id }],
                task_responsible_users: [],
                blockers: null,
                check_list: null,
                intervention,
                transaction_form: null,
              },
            ],
          };

          if (
            intervention.intervention_code.level.levelCode === 1 &&
            !!this.prescription?.doctor?.id
          )
            task.task_entities.push({ entity_id: this.prescription.doctor.id });
          if (this.data.patient?.facility_id)
            task.task_entities.push({ entity_id: this.data.patient.facility_id });
          if (this.data.drp.category === 'SA') task.steps[0].priority = TaskPriority.HIGH;

          this.taskService
            .createTaskV3(task)
            .pipe(take(1))
            .subscribe((res2) => {
              if (!res2?.data) return;

              const mapping = res2.data.steps
                ?.filter((s) => s.default_view === TaskDefaultView.INTERVENTION)
                .map((s) => ({ task_id: s.id, intervention_id: intervention.id }));

              this.medReviewService
                .mapInterventionToTask(mapping, this.data.patient?.id, this.data.drp.id)
                .pipe(take(1), retry(3))
                .subscribe();
            });
        });
      }
    }
  }

  groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  filterTemplateFn(template: any): string {
    return template ? template.name : '';
  }

  onTemplateChange(val) {
    this.manageForm.get('template').setValue(val);
    this.manageForm.get('template').updateValueAndValidity();
  }
}
